
import { useEffect, useState, useCallback, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import stickyRegistry from '@buzzfeed/bf-utils/lib/sticky-registry';
import { useComponentSize, useIntersection } from '@buzzfeed/react-components';
import { AdsContext } from '@buzzfeed/adlib/dist/module/bindings/react/contexts';
import DestinationContext from '../../../../contexts/destination';
import { useTranslation } from '../../../../i18n';
import SidebarAdUnit from '../../../Ads/components/SidebarAd';
import createSidebarAdManager from '../../../Ads/managers/SidebarAdManager';
import SidebarDensity from '../../components/SidebarDensity';
import styles from '../../sidebar.module.scss';
import {
  container,
  transitioning,
  transitionDuration,
} from '../SidebarPaginated/sidebar-paginated.module.scss';

function useSidebarPagination(buzz) {
  const [page, _setPage] = useState(0);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const setPage = useCallback(
    newPage => {
      if (newPage === 0) {
        _setPage(0);
        return;
      }

      setIsPageChanging(true);
      setTimeout(() => {
        _setPage(newPage);
        setIsPageChanging(false);
      }, parseInt(transitionDuration, 10));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buzz.id]
  );

  return [page, setPage, isPageChanging];
}

function SidebarGenerator({ buzz, containerRef, pixiedust }) {
  // Debug flag - set to true to see debug info
  const DEBUG = false;
  
  const adsContext = useContext(AdsContext);
  const { destination } = useContext(DestinationContext);
  const { t } = useTranslation('common');
  const [page, setPage, isPageChanging] = useSidebarPagination(buzz);
  const { height: maxHeight } = useComponentSize(containerRef);
  const { isIntersecting, setObservable } = useIntersection({
    threshold: 0.5,
    pageId: buzz.id,
  });

  useEffect(() => {
    setPage(0);
  }, [setPage]);

  // Create separate refs for before and after generator sidebars
  const beforeSidebarRef = useRef(null);
  const afterSidebarRef = useRef(null);

  // Find the AI generator subbuzz index
  const generatorIndex = useMemo(() => {
    const index = buzz?.sub_buzzes?.findIndex(sb => sb.format_name === 'ai_generator') ?? -1;
    if (DEBUG) {
      console.log('🔍 Generator Detection:', {
        buzzId: buzz?.id,
        totalSubbuzzes: buzz?.sub_buzzes?.length,
        generatorIndex: index,
        subbuzzFormats: buzz?.sub_buzzes?.map((sb, i) => ({ index: i, format: sb.format_name }))
      });
    }
    return index;
  }, [buzz]);

  // State for tracking generator DOM position
  const [generatorBounds, setGeneratorBounds] = useState({ top: 0, bottom: 0 });
  const observerRef = useRef(null);

  useEffect(() => {
    setPage(0);
  }, [setPage]);

  // Observe the generator element position
  useEffect(() => {
    if (generatorIndex === -1) return;

    let debounceTimer = null;

    const updateGeneratorBounds = () => {
      // Debounce to avoid too many recalculations
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {

        // Look for the full-width generator wrapper
        const generatorElements = document.querySelectorAll('.subbuzz-full-width');
        let generatorElement = null;

        // Find the AI generator specifically
        generatorElements.forEach((el) => {
          const aiGeneratorEl = el.querySelector('.subbuzz-bfp--ai_generator');
          if (aiGeneratorEl) {
            generatorElement = el;
          }
        });

        if (generatorElement) {
          const rect = generatorElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          const bounds = {
            top: rect.top + scrollTop,
            bottom: rect.bottom + scrollTop
          };

          setGeneratorBounds(bounds);
        }
      }, 200); // 200ms debounce
    };

    // Initial measurement
    const timer = setTimeout(updateGeneratorBounds, 1000); // Wait for content to load

    // Set up ResizeObserver to track changes
    if (window.ResizeObserver) {
      observerRef.current = new ResizeObserver(updateGeneratorBounds);

      // Observe the main content area
      const contentArea = document.querySelector('.subbuzzes-wrapper');
      if (contentArea) {
        observerRef.current.observe(contentArea);
      }

      // Also observe any existing ad containers
      const adContainers = document.querySelectorAll('[id*="google_ads"], .ad, .adunit');
      adContainers.forEach(container => {
        if (observerRef.current) {
          observerRef.current.observe(container);
        }
      });
    }

    // Set up MutationObserver to catch DOM changes (like ad insertion)
    let mutationObserver = null;
    if (window.MutationObserver) {
      mutationObserver = new MutationObserver((mutations) => {
        const hasLayoutChanges = mutations.some(mutation =>
          mutation.type === 'childList' ||
          (mutation.type === 'attributes' && ['style', 'class'].includes(mutation.attributeName))
        );

        if (hasLayoutChanges) {
          updateGeneratorBounds();
        }
      });
    }

    // Observe the sidebar and content areas for changes
    if (mutationObserver) {
      const contentArea = document.querySelector('.subbuzzes-wrapper');
      const sidebarAreas = document.querySelectorAll('[class*="sidebar"]');

      if (contentArea) {
        mutationObserver.observe(contentArea, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      }

      sidebarAreas.forEach(sidebar => {
        mutationObserver.observe(sidebar, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      });
    }

    // Listen for ad-specific events that might cause layout shifts
    const handleAdLayoutChange = () => {
      updateGeneratorBounds();
    };

    // Listen for various ad events
    const adEvents = [
      'gpt:slotRenderEnded',
      'ad-content-ready',
      'ad-rendered',
      'AdLoaded',
      'AdRendered'
    ];

    adEvents.forEach(eventName => {
      window.addEventListener(eventName, handleAdLayoutChange);
      document.addEventListener(eventName, handleAdLayoutChange);
    });

    // Also listen to scroll and resize events as fallback
    window.addEventListener('resize', updateGeneratorBounds);
    window.addEventListener('load', updateGeneratorBounds);

    // Periodic recalculation as final safety net (every 5 seconds for first 30 seconds)
    const periodicTimer = setInterval(() => {
      updateGeneratorBounds();
    }, 5000);

    // Stop periodic recalculation after 30 seconds
    const stopPeriodicTimer = setTimeout(() => {
      clearInterval(periodicTimer);
    }, 30000);

    return () => {
      clearTimeout(timer);
      clearTimeout(debounceTimer);
      clearInterval(periodicTimer);
      clearTimeout(stopPeriodicTimer);

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (mutationObserver) {
        mutationObserver.disconnect();
      }

      window.removeEventListener('resize', updateGeneratorBounds);
      window.removeEventListener('load', updateGeneratorBounds);

      adEvents.forEach(eventName => {
        window.removeEventListener(eventName, handleAdLayoutChange);
        document.removeEventListener(eventName, handleAdLayoutChange);
      });
    };
  }, [generatorIndex, buzz.id]);

  // Create single ad manager like SidebarPaginated
  const [adManager, setAdManager] = useState(null);

  useEffect(() => {
    if (adsContext.status !== 'loaded') {
      if (DEBUG) console.log('📭 Ads context not loaded yet:', adsContext.status);
      return;
    }

    if (DEBUG) console.log('🚀 Creating ad manager for generator sidebar...');
    
    const manager = createSidebarAdManager(buzz);

    manager.init().then(() => {
      if (DEBUG) console.log('✅ Ad manager initialized successfully');
      setAdManager(manager);
    });

    return () => {
      if (DEBUG) console.log('🧹 Cleaning up ad manager');
      setAdManager(null);
      manager.destroy();
    };
  }, [adsContext, buzz]);

  const [topAdjustment, setTopAdjustment] = useState(0);
  const [afterTopAdjustment, setAfterTopAdjustment] = useState(0);

  const beforeStickyHandler = useCallback(() => {
    if (!beforeSidebarRef.current) {
      return;
    }
    const top = stickyRegistry.getAvailableTop(beforeSidebarRef.current);
    if (topAdjustment !== top) {
      setTopAdjustment(top);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buzz.id]);

  const afterStickyHandler = useCallback(() => {
    if (!afterSidebarRef.current) {
      return;
    }
    const top = stickyRegistry.getAvailableTop(afterSidebarRef.current);
    if (afterTopAdjustment !== top) {
      setAfterTopAdjustment(top);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buzz.id]);

  // Check with the global sticky registry for top adjustments
  useEffect(() => {
    const isGeneratorDetected = generatorIndex !== -1 && (generatorBounds.top > 0 || generatorBounds.bottom > 0);

    // Always try to handle before sidebar
    if (beforeSidebarRef.current) {
      beforeStickyHandler();
      stickyRegistry.subscribe(beforeStickyHandler);
    }

    // Only handle after sidebar if generator is actually detected
    if (isGeneratorDetected && afterSidebarRef.current) {
      afterStickyHandler();
      stickyRegistry.subscribe(afterStickyHandler);
    }

    return () => {
      stickyRegistry.unsubscribe(beforeStickyHandler);
      if (isGeneratorDetected) {
        stickyRegistry.unsubscribe(afterStickyHandler);
      }
    };
  }, [buzz.id, beforeStickyHandler, afterStickyHandler, generatorIndex, generatorBounds]);

  // Calculate positions based on actual generator bounds
  const beforeGeneratorHeight = useMemo(() => {
    if (generatorIndex === -1 || generatorBounds.top === 0) {
      return maxHeight;
    }

    // Get the article container position to calculate relative positioning
    const articleContainer = document.querySelector('.embed-content');
    const articleTop = articleContainer ? articleContainer.getBoundingClientRect().top + window.pageYOffset : 0;

    // Height should be from start of sidebar to start of generator minus some margin
    const relativeGeneratorTop = generatorBounds.top - articleTop;
    const calculatedHeight = Math.max(relativeGeneratorTop - 50, 200); // 50px margin before generator, minimum 200px

    if (DEBUG) {
      console.log('📏 Before generator height calculation:', {
        generatorBounds,
        articleContainer: !!articleContainer,
        articleTop,
        relativeGeneratorTop,
        calculatedHeight,
        maxHeight
      });
    }

    return calculatedHeight;
  }, [generatorIndex, generatorBounds.top, maxHeight]);

    // Calculate height for after-generator sidebar
  const afterGeneratorHeight = useMemo(() => {
    if (generatorIndex === -1 || generatorBounds.top === 0) {
      return maxHeight; // Use full height as fallback
    }
    
    const height = maxHeight;

    if (DEBUG) {
      console.log('📏 After generator height calculation (simple):', {
        maxHeight,
        beforeGeneratorHeight,
        generatorHeight: generatorBounds.bottom - generatorBounds.top,
        usingHeight: height,
        reason: 'Using maxHeight like first sidebar'
      });
    }

    return height;
  }, [generatorIndex, generatorBounds, maxHeight]);

  const afterGeneratorTop = useMemo(() => {
    if (generatorIndex === -1 || generatorBounds.bottom === 0) {
      return 0;
    }

    // Get the article container position to calculate relative positioning
    const articleContainer = document.querySelector('.embed-content');
    const articleTop = articleContainer ? articleContainer.getBoundingClientRect().top + window.pageYOffset : 0;

    // Position should be after generator ends plus some margin
    const relativeGeneratorBottom = generatorBounds.bottom - articleTop;
    const calculatedTop = relativeGeneratorBottom + 50; // 50px margin after generator

    return calculatedTop;
  }, [generatorIndex, generatorBounds.bottom]);

  const isGeneratorDetected = generatorIndex !== -1 && generatorBounds.top > 0 && generatorBounds.bottom > 0;

  if (!adManager) return null;

  if (DEBUG) {
    console.log('🎨 Rendering generator sidebars:', {
      generatorIndex,
      generatorBounds,
      isGeneratorDetected,
      topAdjustment,
      afterTopAdjustment,
      beforeGeneratorHeight,
      afterGeneratorHeight,
      afterGeneratorTop,
      maxHeight,
      generatorHeight: generatorBounds.bottom - generatorBounds.top,
      currentPage: page,
      adManager: !!adManager,
      isIntersecting
    });
  }

  return (
    <>
      {/* Debug overlay showing generator position */}
      {DEBUG && generatorBounds.top > 0 && (
        <div style={{
          position: 'fixed',
          top: '50px',
          left: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px',
          zIndex: 9999,
          fontSize: '11px',
          maxWidth: '300px'
        }}>
          <div><strong>GENERATOR DEBUG</strong></div>
          <div>Index: {generatorIndex}</div>
          <div>Bounds: {generatorBounds.top} - {generatorBounds.bottom}</div>
          <div>Before Height: {beforeGeneratorHeight}px</div>
          <div>After Top: {afterGeneratorTop}px</div>
          <div>Before Top Adj: {topAdjustment}px</div>
          <div>After Top Adj: {afterTopAdjustment}px</div>
          <div>Max Height: {maxHeight}px</div>
          <div>Page: {page}</div>
          <div>Before Sidebar: ALWAYS ACTIVE (showing page {Math.floor(page / 2) * 2})</div>
          <div>After Sidebar: ALWAYS ACTIVE (showing page {Math.floor(page / 2) * 2 + 1})</div>
          <div>After MarginTop: {afterGeneratorTop}px</div>
          <div>After Height: {afterGeneratorHeight}px</div>
        </div>
      )}

      {/* First sidebar section - before generator */}
      <section
        className={`${styles.sidebar} ${styles[destination]} ${
          buzz.isAd ? styles.sidebarBranded : ''
        }`}
        aria-label={`${t('promoted_content')} - Before Generator`}
        ref={beforeSidebarRef}
        style={{
          maxHeight: `${beforeGeneratorHeight}px`,
          overflow: 'visible', // Allow sticky positioning to work
          ...(DEBUG && { 
            border: '3px solid red', 
            background: 'rgba(255,0,0,0.1)',
            boxSizing: 'border-box'
          })
        }}
      >
        <div
          ref={setObservable}
          className={`${container} ${isPageChanging ? transitioning : ''}`}
          style={{
            top: `${topAdjustment}px`
          }}
        >
          {DEBUG && (
            <div style={{ 
              background: 'red', 
              color: 'white', 
              padding: '5px', 
              fontSize: '10px',
              textAlign: 'center'
            }}>
              BEFORE GENERATOR AD (Max: {beforeGeneratorHeight}px, Page: {Math.floor(page / 2) * 2}, Active: YES)
            </div>
          )}
          {/* First sidebar always shows even pages (0, 2, 4...) */}
          <SidebarDensity
            sidebarPage={Math.floor(page / 2) * 2}
            onNextPageReady={() => setPage(p => p + 1)}
            maxHeight={beforeGeneratorHeight}
            isIntersecting={isIntersecting}
            buzzId={`${buzz.id}-before`}
            adManager={adManager}
          >
            <SidebarAdUnit
              adManager={adManager}
              pixiedust={{
                ...pixiedust,
                unit_name: `${pixiedust.unit_name}_before_generator`,
                position_in_unit: 0
              }}
            />
            <SidebarAdUnit
              adManager={adManager}
              pixiedust={{
                ...pixiedust,
                unit_name: `${pixiedust.unit_name}_before_generator`,
                position_in_unit: 1
              }}
            />
          </SidebarDensity>
        </div>
      </section>

      {/* Second sidebar section - after generator */}
      <section
        className={`${styles.sidebar} ${styles[destination]} ${
          buzz.isAd ? styles.sidebarBranded : ''
        }`}
        aria-label={`${t('promoted_content')} - After Generator`}
        ref={afterSidebarRef}
        style={{
          transform: 'translateY(0) translateX(-430px)',
          marginTop: `${afterGeneratorTop}px`,
          height: `${afterGeneratorHeight}px`, // Calculated remaining space after first sidebar and generator
          overflow: 'visible', // Allow sticky positioning to work
          ...(DEBUG && { 
            border: '3px solid green', 
            background: 'rgba(0,255,0,0.1)',
            boxSizing: 'border-box'
          })
        }}
      >
        <div
          className={`${container} ${isPageChanging ? transitioning : ''}`}
          ref={setObservable}
          style={{
            top: '60px'
          }}
        >
          {DEBUG && (
            <div style={{ 
              background: 'green', 
              color: 'white', 
              padding: '5px', 
              fontSize: '10px',
              textAlign: 'center'
            }}>
              AFTER GENERATOR AD (Height: {afterGeneratorHeight}px, Page: {Math.floor(page / 2) * 2 + 1}, Active: {isGeneratorDetected ? 'YES' : 'NO'}, MarginTop: {afterGeneratorTop}px)
            </div>
          )}
          {/* Second sidebar always shows odd pages (1, 3, 5...) when generator detected */}
          {isGeneratorDetected && <SidebarDensity
            sidebarPage={Math.floor(page / 2) * 2 + 1}
            onNextPageReady={() => setPage(p => p + 1)}
            maxHeight={afterGeneratorHeight}
            isIntersecting={isIntersecting}
            buzzId={`${buzz.id}-after`}
            adManager={adManager}
          >
            <SidebarAdUnit
              adManager={adManager}
              pixiedust={{
                ...pixiedust,
                unit_name: `${pixiedust.unit_name}_after_generator`,
                position_in_unit: 0
              }}
            />
            <SidebarAdUnit
              adManager={adManager}
              pixiedust={{
                ...pixiedust,
                unit_name: `${pixiedust.unit_name}_after_generator`,
                position_in_unit: 1
              }}
            />
          </SidebarDensity>}
        </div>
      </section>
    </>
  );
}

SidebarGenerator.propTypes = {
  buzz: PropTypes.object,
  containerRef: PropTypes.shape({ current: PropTypes.object }),
  pixiedust: PropTypes.object,
};

export default SidebarGenerator;
